import {useState} from 'react';
import {DateTime} from 'luxon';

import {
    ChangeRequest, School
} from '../../../api/types/__generated__/data-contracts';
import {useSnackbar} from '../../../components/snackbars/snackbars';
import {useMutationSetSchoolAcademicYear} from '../../../hooks/mutations/use-mutation-set-school-academic-year';
import {DEFAULT_STATE} from '../../../api/mock/generate/state/mock-school.state';

type ModalState = {
    isOpen: boolean
    isLoading: boolean
    school: null | School
}

const defaultState = DEFAULT_STATE;


export const useAcademicYearChangeModal = () => {
    const [state, setState] = useState<ModalState>(defaultState);
    const {
        mutateAsync, isLoading, isError, reset
    } = useMutationSetSchoolAcademicYear();
    const { enqueueSnackbar } = useSnackbar();

    const handleClose = () => {
        if (state.isLoading) return;
        setState(defaultState);
    };

    const handleSubmitChangeEndDate = async (endDate: DateTime) => {
        if (!state.school || !state.school.schoolId) {
            console.error('schoolId is required for changing academic year', state.school);
            return;
        }

        // Set the time to the end of the day,
        // existing entries in the db are all done this way.
        const endDateTime = endDate
            .set({ hour: 23, minute: 59, second: 59 })
            .toISO({
                // expected format eg 2021-07-25T23:59:59Z
                includeOffset: true,
                suppressMilliseconds: true,
                includePrefix: false
            });

        const changeRequestPayload: ChangeRequest = {
            endDate: endDateTime,
            schoolId: state.school.schoolId
        };

        // API business rule re change / add academic year for PUT api
        // eg provide the backend an academicYearId if the isSchoolAcademicYear value is true
        if (state.school.academicYear?.isSchoolAcademicYear) {
            changeRequestPayload.schoolAcademicYearId = state.school.academicYear.academicYearId;
        }
        // store for snackbar message
        const schoolUpdated = { ...state.school };

        try {
            await mutateAsync(changeRequestPayload);

            setState({
                isOpen: true,
                isLoading: true,
                school: null
            });

            enqueueSnackbar(
                // eslint-disable-next-line max-len
                `Successfully updated CRMID: ${schoolUpdated.crmId} School: ${schoolUpdated.name}.`, {
                    variant: 'success'
                });
        } catch {
            // default onError in useMutationSetSchoolAcademicYear
        }

        reset();
        handleClose();
    };

    const handleOpenChangeSchool = (school: School) => {
        setState({
            isLoading: false,
            isOpen: true,
            school
        });
    };

    return {
        isLoading,
        isError,
        onOpenChangeSchool: handleOpenChangeSchool,
        onSubmitChangeEndDate: handleSubmitChangeEndDate,
        onClose: handleClose,
        isOpen: state.isOpen,
        school: state.school
    };
};
